import React, { useState, useEffect, useContext, useRef } from "react"; 
import { Modal } from "react-bootstrap"; 
import { LangContext } from "../../context/lang-context"; 
import sampleProduct from "../../assets/images/sample-product.svg"; 
 
const DEFAULT_CATEGORY_COLOR = "#6C757D"; 
 
const normalizeColor = (value) => { 
  const color = String(value || "").trim(); 
 
  return /^#[0-9A-F]{6}$/i.test(color) 
    ? color.toUpperCase() 
    : DEFAULT_CATEGORY_COLOR; 
}; 
 
const isValidColor = (value) => { 
  return /^#[0-9A-F]{6}$/i.test(String(value || "").trim()); 
}; 

const getImageFileName = (path) => {
  if (!path) return "";

  const normalizedPath = String(path).replace(/\\/g, "/");
  const storedFileName = normalizedPath.split("/").pop() || "";

  return storedFileName.replace(
    /^\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2}\.\d{3}Z-/,
    ""
  );
};
 
export default function CategoryForm({ 
  API_URL, 
  categoryData, 
  handleSubmit, 
  handleDelete, 
  form, 
  setForm, 
}) { 
  const { 
    TRL_Pack: { categories }, 
  } = useContext(LangContext); 
 
  const fileRef = useRef(null); 
 
  const [imagePreview, setImagePreview] = useState(sampleProduct); 
  const [selectedFileName, setSelectedFileName] = useState(""); 
  const [hasNewImage, setHasNewImage] = useState(false); 
  const [imageRemoved, setImageRemoved] = useState(false);
 
  const [name, setName] = useState(""); 
  const [color, setColor] = useState(""); 
 
  const isShared = !!categoryData?.IsShared; 
  const isNew = form === "new"; 
  const show = !!form; 
 
  const close = () => setForm(null); 
 
  useEffect(() => { 
    if (!show) return; 
 
    setName(categoryData?.Name || ""); 
 
    if (isNew) { 
      setColor(DEFAULT_CATEGORY_COLOR); 
    } else { 
      setColor(categoryData?.Color || ""); 
    } 
 
    setHasNewImage(false); 
    setImageRemoved(false);
 
    if (fileRef.current) { 
      fileRef.current.value = ""; 
    } 
 
    if (categoryData?.Image) { 
      setImagePreview(`${API_URL}/${categoryData.Image}`);
      setSelectedFileName(getImageFileName(categoryData.Image));
    } else { 
      setImagePreview(sampleProduct);
      setSelectedFileName("");
    } 
  }, [
    show,
    form,
    API_URL,
    isNew,
    categoryData?.CategoryId,
    categoryData?.Name,
    categoryData?.Color,
    categoryData?.Image,
  ]); 
 
  const handleChangeImage = (evt) => { 
    const file = evt.target.files?.[0]; 
 
    if (!file) return; 
 
    setSelectedFileName(file.name); 
    setHasNewImage(true); 
 
    const reader = new FileReader(); 
 
    reader.readAsDataURL(file); 
 
    reader.onloadend = () => { 
      setImagePreview(reader.result); 
    }; 
  }; 
 
  const handleRemoveImage = () => { 
    if (isShared) return;

    if (hasNewImage) {
      setHasNewImage(false);

      if (fileRef.current) {
        fileRef.current.value = "";
      }

      if (categoryData?.Image && !imageRemoved) {
        setImagePreview(`${API_URL}/${categoryData.Image}`);
        setSelectedFileName(
          getImageFileName(categoryData.Image)
        );
      } else {
        setImagePreview(sampleProduct);
        setSelectedFileName("");
      }

      return;
    }

    if (categoryData?.Image && !imageRemoved) {
      setImageRemoved(true);
      setSelectedFileName("");
      setImagePreview(sampleProduct);

      if (fileRef.current) {
        fileRef.current.value = "";
      }
    }
  }; 
 
  const disableSubmit = (() => { 
    if (isShared) return true; 
 
    const trimmedName = String(name || "").trim(); 
    const trimmedColor = String(color || "").trim(); 
 
    if (!trimmedName) return true; 
 
    if (!isValidColor(trimmedColor)) return true; 
 
    if (isNew) return false; 
 
    const originalName = String(categoryData?.Name || "").trim(); 
    const originalColor = String(categoryData?.Color || "") 
      .trim() 
      .toUpperCase(); 
 
    const colorChanged = 
      trimmedColor.toUpperCase() !== originalColor; 
 
    const nameChanged = 
      trimmedName !== originalName; 
 
    if ( 
      !hasNewImage &&
      !imageRemoved &&
      !nameChanged && 
      !colorChanged 
    ) { 
      return true; 
    } 
 
    return false; 
  })(); 

  const canRemoveImage =
    !isShared &&
    (
      hasNewImage ||
      (!!categoryData?.Image && !imageRemoved)
    );
 
  return ( 
    <Modal 
      show={show} 
      onHide={close} 
      centered 
      backdrop="static" 
    > 
      <div className="modal-content"> 
        <div className="modal-header"> 
          <h5 className="modal-title pe-none"> 
            {categoryData 
              ? categories.editCategoryHeader 
              : categories.newCategoryHeader} 
          </h5> 
 
          <button 
            type="button" 
            className="close" 
            onClick={close} 
          > 
            <span>&times;</span> 
          </button> 
        </div> 
 
        <div className="modal-body"> 
          <div className="text-center mb-3"> 
            <img 
              src={imagePreview} 
              alt="" 
              onError={(e) => { 
                e.currentTarget.src = sampleProduct; 
              }} 
              onClick={ 
                canRemoveImage 
                  ? handleRemoveImage 
                  : undefined 
              } 
              width="256" 
              height="256" 
              style={{ 
                objectFit: "cover", 
                cursor: 
                  canRemoveImage 
                    ? "pointer" 
                    : "default", 
              }} 
            /> 
          </div> 
 
          <form 
            onSubmit={handleSubmit} 
            id="modal-form" 
            autoComplete="off" 
          > 
            <input 
              type="hidden" 
              name="existingImage" 
              value={
                imageRemoved
                  ? ""
                  : categoryData?.Image || ""
              } 
            />

            <input
              type="hidden"
              name="imageRemoved"
              value={imageRemoved ? "1" : "0"}
            />
 
            <div className="form-group"> 
              <label className="h6 ml-1px"> 
                {categories.props.image} 
              </label> 
 
              <div className="input-group"> 
                <div className="custom-file"> 
                  <input 
                    ref={fileRef} 
                    type="file" 
                    className="custom-file-input" 
                    name="image" 
                    onChange={handleChangeImage} 
                    accept="image/x-png,image/svg+xml" 
                    id="image-upload" 
                    disabled={isShared} 
                  /> 
 
                  <label 
                    style={{ fontWeight: "400" }} 
                    className="custom-file-label font-to-poppins" 
                    htmlFor="image-upload" 
                  > 
                    {selectedFileName || "Wybierz plik"} 
                  </label> 
                </div> 
              </div> 
            </div> 
 
            <div className="form-group"> 
              <label className="h6 ml-1px"> 
                {categories.props.categoryName} 
              </label> 
 
              <input 
                name="name" 
                className="form-control" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                disabled={isShared} 
                required 
              /> 
            </div> 
 
            <div className="form-group mb-0"> 
              <label className="h6 ml-1px"> 
                Kolor 
              </label> 
 
              <div 
                className="d-flex align-items-center" 
                style={{ gap: 10 }} 
              > 
                <input 
                  type="text" 
                  className={`form-control ${ 
                    color && !isValidColor(color) 
                      ? "is-invalid" 
                      : "" 
                  }`} 
                  value={color} 
                  onChange={(e) => 
                    setColor(e.target.value.toUpperCase()) 
                  } 
                  onBlur={() => { 
                    if (isValidColor(color)) { 
                      setColor( 
                        String(color).trim().toUpperCase() 
                      ); 
                    } 
                  }} 
                  disabled={isShared} 
                  maxLength={7} 
                  placeholder="#6C757D" 
                  required 
                  style={{ 
                    maxWidth: 130, 
                  }} 
                /> 
 
                <label 
                  style={{ 
                    position: "relative", 
                    width: 38, 
                    height: 38, 
                    flex: "0 0 38px", 
                    margin: 0, 
                    borderRadius: 4, 
                    border: "1px solid #ced4da", 
                    background: normalizeColor(color), 
                    cursor: isShared 
                      ? "not-allowed" 
                      : "pointer", 
                    overflow: "hidden", 
                  }} 
                > 
                  <input 
                    type="color" 
                    name="color" 
                    value={normalizeColor(color)} 
                    onChange={(e) => 
                      setColor( 
                        e.target.value.toUpperCase() 
                      ) 
                    } 
                    disabled={isShared} 
                    style={{ 
                      position: "absolute", 
                      inset: 0, 
                      width: "100%", 
                      height: "100%", 
                      opacity: 0, 
                      cursor: isShared 
                        ? "not-allowed" 
                        : "pointer", 
                    }} 
                  /> 
                </label> 
              </div> 
 
              {color && !isValidColor(color) && ( 
                <small className="text-danger"> 
                  Podaj kolor w formacie HEX, np. #6C757D 
                </small> 
              )} 
            </div> 
          </form> 
        </div> 
 
        {!isShared && ( 
          <div className="modal-footer"> 
            {!isNew && 
              typeof handleDelete === "function" && ( 
                <button 
                  type="button" 
                  className="btn btn-outline-danger mr-auto" 
                  onClick={() => 
                    handleDelete( 
                      categoryData?.CategoryId 
                    ) 
                  } 
                > 
                  Usuń 
                </button> 
              )} 
 
            <button 
              type="submit" 
              form="modal-form" 
              className="btn btn-primary" 
              disabled={disableSubmit} 
            > 
              Zapisz 
            </button> 
          </div> 
        )} 
      </div> 
    </Modal> 
  ); 
}